import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import api from 'services/api'

export interface SavedFile {
  id: number
  originalName: string
  type: string
  url?: string
}

export interface SavedFiles {
  identity_document?: SavedFile
  prescription?: SavedFile
  medical_report?: SavedFile
  lawsuit_term?: SavedFile
}

interface FetchDocumentsResponse {
  documents: SavedFile[]
  success: boolean
}

const DOCUMENT_TYPE_MAP: Record<string, string> = {
  identityDocument: 'IDENTIDADE',
  prescription: 'PRESCRICAO',
  medicalReport: 'LAUDO_MEDICO',
  lawsuitTerm: 'TERMO_AJUIZAMENTO'
}

export default function useAssociationDocuments() {
  const [loading, setLoading] = useState(false)
  const [identityDocument, setIdentityDocument] = useState<File>()
  const [prescription, setPrescription] = useState<File>()
  const [medicalReport, setMedicalReport] = useState<File>()
  const [lawsuitTerm, setLawsuitTerm] = useState<File>()
  const [savedFiles, setSavedFiles] = useState<SavedFiles>({})

  const fetchDocuments = async () => {
    setLoading(true)
    try {
      const { data } = await api.get<FetchDocumentsResponse>('/my-account/documents')

      if (data.success && data.documents) {
        const formattedFiles: SavedFiles = {}

        data.documents.forEach((doc) => {
          if (doc.type === 'IDENTIDADE') {
            formattedFiles.identity_document = doc
          } else if (doc.type === 'PRESCRICAO') {
            formattedFiles.prescription = doc
          } else if (doc.type === 'LAUDO_MEDICO') {
            formattedFiles.medical_report = doc
          } else if (doc.type === 'TERMO_AJUIZAMENTO') {
            formattedFiles.lawsuit_term = doc
          }
        })

        setSavedFiles(formattedFiles)
      }
    } catch (err) {
      console.error('Erro ao carregar documentos:', err)
    } finally {
      setLoading(false)
    }
  }

  const uploadDocument = async (file: File, type: string, fieldName: string) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', type)
    formData.append('emission', new Date().toISOString().split('T')[0])
    formData.append('upload_source', 'site_associacao')

    setLoading(true)
    try {
      await api.post('/my-account/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      toast.success('Documento enviado com sucesso!')

      switch (fieldName) {
        case 'identityDocument':
          setIdentityDocument(undefined)
          break
        case 'prescription':
          setPrescription(undefined)
          break
        case 'medicalReport':
          setMedicalReport(undefined)
          break
        case 'lawsuitTerm':
          setLawsuitTerm(undefined)
          break
        default:
          break
      }

      await fetchDocuments()
    } catch (err) {
      toast.error('Erro ao salvar documento. Tente novamente.')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const changeFieldValue = useCallback((fieldName: string, newValue?: File) => {
    if (!newValue) return

    const documentType = DOCUMENT_TYPE_MAP[fieldName]
    if (!documentType) return

    switch (fieldName) {
      case 'identityDocument':
        setIdentityDocument(newValue)
        break
      case 'prescription':
        setPrescription(newValue)
        break
      case 'medicalReport':
        setMedicalReport(newValue)
        break
      case 'lawsuitTerm':
        setLawsuitTerm(newValue)
        break
      default:
        return
    }

    uploadDocument(newValue, documentType, fieldName)
  }, [])

  const handleSaveDocuments = async () => {
    const pendingUploads: { file: File; type: string }[] = []

    if (identityDocument) pendingUploads.push({ file: identityDocument, type: 'IDENTIDADE' })
    if (prescription) pendingUploads.push({ file: prescription, type: 'PRESCRICAO' })
    if (medicalReport) pendingUploads.push({ file: medicalReport, type: 'LAUDO_MEDICO' })
    if (lawsuitTerm) pendingUploads.push({ file: lawsuitTerm, type: 'TERMO_AJUIZAMENTO' })

    if (pendingUploads.length === 0) {
      toast.warning('Selecione pelo menos um documento para enviar')
      return
    }

    setLoading(true)
    try {
      await Promise.all(
        pendingUploads.map(({ file, type }) => {
          const formData = new FormData()
          formData.append('file', file)
          formData.append('type', type)
          formData.append('emission', new Date().toISOString().split('T')[0])
          formData.append('upload_source', 'site_associacao')

          return api.post('/my-account/documents', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        })
      )

      toast.success('Documentos salvos com sucesso!')

      setIdentityDocument(undefined)
      setPrescription(undefined)
      setMedicalReport(undefined)
      setLawsuitTerm(undefined)

      await fetchDocuments()
    } catch (err) {
      toast.error('Erro ao salvar documentos. Tente novamente.')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDocuments()
  }, [])

  return {
    loading,
    identityDocument,
    prescription,
    medicalReport,
    lawsuitTerm,
    savedFiles,
    changeFieldValue,
    handleSaveDocuments
  }
}
